import React from 'react';
import styled from 'styled-components';
import { DynamicAssets, DynamicAssetsKeys, NavItem, ValuePropositionContent } from './models';
import { getPartnerResource } from '.';
import HeroTitle from './components/HeroTitle';
import HeroSubtitle from './components/HeroSubtitle';
import CalculatorHeader from './components/CalculatorHeader';
import { ProtectItCta } from './components';
import { DisclaimerLink } from '../components/Disclaimer';
import { formatDollars } from '../pages/calculator/utils';
import BREAK_POINTS from '../styling/media-queries';
import HeroImageContainer from './components/HeroImageContainer';
import ProtectItTitle from './components/ProtectItTitle';
import ProtectItContainer from './components/ProtectItContainer';
import ProtectItTextContainer from './components/ProtectItTextContainer';
import ValuePropTitle from './components/ValuePropTitle';
import CalculatorResultsHeader from './components/CalculatorResultsHeader';
import FinePrintTitle from './components/FinePrintTitle';
import TestimonialTitle from './components/TestimonialTitle';
import MoreInformationLink from './components/ValuePropositionLink';
import ValuePropositionImageContainer from './components/ValuePropositionImageContainer';
import EnrollmentTitle from './components/EnrollmentTitle';
import HeroContentSpacer from './components/HeroContentSpacer';
import SignupImageContainer from './components/SignupImageContainer';
import CompetitorPriceCell from './components/CalculatorCompetitorPriceCell';
import signupImage1 from './resources/signup-image-1.jpg';
import signupImage2 from './resources/signup-image-2.jpg';
import signupImage3 from './resources/signup-image-3.jpg';
import signupImage4 from './resources/signup-image-4.jpg';
import signupImage5 from './resources/signup-image-5.jpg';
import WhatsCoveredCallToActionTitle from './components/WhatsCoveredCallToActionTitle';
import ConfirmationCallButton from './components/ConfirmationCallButton';
import HeaderCallToAction from './components/HeaderCallToAction';
import { RESIDENCE_PAGE_NUMBER, REVIEW_PAGE_NUMBER, LinkId } from '../constants';
import { getTotalPurchaseAmount } from '../pages/signup/utils';
import { AuthPath } from '../pages/auth/models';
import { getPath } from '../pages/auth/utils';
import { openChat } from '../utils';
import { getPartner } from '../partner';

const PRODUCT_NAME = 'Sprint Complete MyHome';
const CHAT_URL = 'https://myhome.techhero.live/askmyexpert/login';
const { claimUrl, expertSessionsPath } = getPartner();

const navLinks: NavItem[] = [
  {
    id: LinkId.HeaderMenuExpertSessions,
    label: 'Mini tech sessions',
    to: expertSessionsPath,
  },
  {
    id: LinkId.HeaderMenuChat,
    label: 'Live help',
    onClick: () => openChat(),
  },
  {
    id: LinkId.HeaderMenuWhatsCovered,
    label: 'What\u2019s covered?',
    to: '/whats-covered',
  },
  {
    id: LinkId.HeaderMenuClaims,
    label: 'File a claim',
    to: claimUrl,
  },
  {
    id: LinkId.HeaderMenuLogin,
    label: 'Log in',
    to: getPath(AuthPath.Login),
  },
];

const loggedInHeaderLinks: NavItem[] = [
  {
    id: LinkId.HeaderMenuWhatsCovered,
    label: 'What\u2019s covered?',
    to: '/whats-covered',
  },
  {
    id: LinkId.HeaderMenuFaq,
    label: 'FAQs',
    to: '/faq',
  },
  {
    id: LinkId.HeaderMenuDashboard,
    label: 'Dashboard',
    to: '/dashboard',
  },
  {
    id: LinkId.HeaderMenuLogout,
    label: 'Log out',
    to: getPath(AuthPath.Logout),
  },
];

const footerLinks: NavItem[] = [
  {
    id: LinkId.FooterMenuExpertSessions,
    label: 'Mini tech sessions',
    to: expertSessionsPath,
  },
  {
    id: LinkId.FooterMenuChat,
    label: 'Live help',
    onClick: () => openChat(),
  },
  {
    id: LinkId.FooterMenuWhatsCovered,
    label: 'What\u2019s covered?',
    to: '/whats-covered',
  },
  {
    id: LinkId.FooterMenuFaq,
    label: 'FAQ',
    to: '/faq',
  },
  {
    id: LinkId.FooterMenuClaims,
    label: 'File a claim',
    to: claimUrl,
  },
  {
    id: LinkId.FooterMenuLogin,
    label: 'Log in',
    to: getPath(AuthPath.Login),
  },
];

export const valuePropositionContent: ValuePropositionContent[] = [
  {
    icon: getPartnerResource('get-refund-icon.svg'),
    caption: 'Get money back',
    title: (
      <>
        See if you can get some <br /> money back from old <br /> tech warranties
      </>
    ),
    subtitle: (
      <>
        With Sprint Complete MyHome, you may
        <br /> not need your old tech warranties
        <br /> anymore.
      </>
    ),
    points: [],
    image: getPartnerResource('get-refund-image.jpg'),
    imageMargins: { desktop: { right: true, bottom: true, top: true }, mobile: {} },
    contentMargins: { desktop: { left: true, bottom: true, top: true }, mobile: {} },
    link: <MoreInformationLink light={true} text="Show me how" to="/dashboard/warranty-selection" />,
    shouldShowBanner: false,
    label: 'refund',
  },
  {
    icon: getPartnerResource('value-prop-icon-1.svg'),
    caption: 'Protection & repairs',
    title: <>Worry-free protection</>,
    subtitle: <>Because accidents can happen to anyone.</>,
    points: [
      'Home technology products covered, regardless of age',
      'Portable devices covered from drops and spills',
      <>Can&rsquo;t repair? We&rsquo;ll try to replace</>,
      <>Can&rsquo;t replace? We&rsquo;ll send a check for current replacement value</>,
    ],
    image: getPartnerResource('value_prop_img_1.jpg'),
    imageMargins: { desktop: { left: true, bottom: true, top: true }, mobile: {} },
    contentMargins: { desktop: { right: true, bottom: true, top: true }, mobile: {} },
    link: <MoreInformationLink light={false} text="See everything we cover" to="/whats-covered" />,
  },
  {
    icon: getPartnerResource('value-prop-icon-2.svg'),
    caption: 'Unlimited expert support',
    title: 'Experts you can trust and understand',
    subtitle: <>We&rsquo;ve got your back.</>,
    points: [
      'No transfers, no tech jargon, no additional fees',
      'Unlimited calls or chats',
      'Millions of happy callers every month',
      'Our average rating is 4.8 out of 5 stars',
    ],
    image: getPartnerResource('value_prop_img_2.jpg'),
    imageMargins: { desktop: { right: true, top: true, bottom: true }, mobile: {} },
    contentMargins: { desktop: { left: true, top: true, bottom: true }, mobile: {} },
    // link: <MoreInformationLink text="See how support works" />,
  },
  {
    icon: getPartnerResource('value-prop-icon-3.svg'),
    caption: 'No paperwork or receipts',
    title: <>All of the coverage</>,
    subtitle: <>None of the hassle.</>,
    points: [
      <>It doesn&rsquo;t matter where or when you bought your device</>,
      'No expiration dates to keep track of',
      'No receipts to hunt down',
      'Already covered? We can help you find out about refund eligibility',
    ],
    image: getPartnerResource('value_prop_img_3.jpg'),
    imageMargins: { desktop: { left: true, top: true, bottom: true }, mobile: {} },
    contentMargins: { desktop: { right: true, top: true, bottom: true }, mobile: {} },
    link: <MoreInformationLink light={false} text="See everything we cover" to="/whats-covered" />,
  },
  {
    icon: getPartnerResource('value-prop-icon-4.svg'),
    caption: 'Learn from the best',
    title: '1:1 mini tech sessions',
    subtitle: 'Get smart about your home.',
    points: [
      'Personalized learning with subject matter experts',
      'Pick from a menu of topics or suggest your own',
      'Enjoy learning from the comfort of home',
    ],
    image: getPartnerResource('value_prop_img_4.jpg'),
    imageMargins: { desktop: { right: true, top: true, bottom: true }, mobile: {} },
    contentMargins: { desktop: { left: true, top: true, bottom: true }, mobile: { bottom: true } },
    link: <MoreInformationLink light text="Schedule a session" to={expertSessionsPath} />,
  },
];

const ProtectItSubtitle = (props: { maxPrice: number }) => {
  const { maxPrice } = props;
  return (
    <>
      {maxPrice > 0 ? (
        <>
          Individual protection plans could cost you <strong>{formatDollars(Math.round(maxPrice * 24), 0)}</strong>
          <DisclaimerLink color="white">2</DisclaimerLink> and they&rsquo;re a hassle to keep up with.
        </>
      ) : (
        <>Individual protection can be limited and a hassle to keep up with.</>
      )}
      <>
        {' '}
        We&rsquo;ve thrown out the old model to give you more for less: protection, tech support, and none of the
        runaround.
      </>
    </>
  );
};

const CalculatorLogoContainer = styled.div`
  font-weight: 600;
  text-align: center;
  font-size: 14px;
  @media ${BREAK_POINTS.mobileBig} {
    font-size: 18px;
  }
`;

const CalculatorLogo = () => <CalculatorLogoContainer>{PRODUCT_NAME}</CalculatorLogoContainer>;

const HOME_CALL_TO_ACTION = `Join now`;

const SignupSubtitle = styled.div`
  margin-bottom: 4px;
  font-weight: 600;
  color: white;
  font-size: 16px;
  text-align: center;
  @media ${BREAK_POINTS.desktop} {
    font-size: 18px;
  }
`;

const ProductValueBackground = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 0;
  width: 100%;
  @media ${BREAK_POINTS.desktop} {
    width: auto;
  }
`;

const ProductValueTextContainer = styled.div`
  z-index: 0;
  padding: 12px;
  width: fill-available;
  display: flex;
  justify-content: center;
  flex-direction: column;
  align-items: center;
  @media ${BREAK_POINTS.desktop} {
    padding: 25px;
    max-width: initial;
  }
`;

const getPriceToDisplay = (signupStep: number, price: number, tax: number) =>
  signupStep <= RESIDENCE_PAGE_NUMBER ? price : getTotalPurchaseAmount(price, tax);

const PricingTextBold = styled.span`
  font-weight: 600;
  font-size: 16px;
  color: white;
`;

export type ProductValueProps = {
  step: number;
  price: number;
  tax: number;
  subtitle: string;
};

const SignupPricingComponent: React.SFC<ProductValueProps> = (props) => {
  const shouldShowPrice = props.step > RESIDENCE_PAGE_NUMBER && props.step < REVIEW_PAGE_NUMBER;
  const pricingText = `$${getPriceToDisplay(props.step, props.price, props.tax)}/month`;

  return (
    <ProductValueBackground>
      <ProductValueTextContainer>
        <SignupSubtitle>{props.subtitle}</SignupSubtitle>
        {shouldShowPrice && <PricingTextBold>{pricingText}</PricingTextBold>}
      </ProductValueTextContainer>
    </ProductValueBackground>
  );
};

const assets: DynamicAssets = {
  [DynamicAssetsKeys.contactAgreementContainer]: () => null,
  [DynamicAssetsKeys.chatUrl]: CHAT_URL,
  [DynamicAssetsKeys.expertSessionsPath]: expertSessionsPath,
  [DynamicAssetsKeys.expertSessionsCallMeLaterSubtitle]: '',
  [DynamicAssetsKeys.loggedInFooterNavLinks]: footerLinks,
  [DynamicAssetsKeys.footerNavLinks]: footerLinks,

  [DynamicAssetsKeys.loggedInHeaderNavLinks]: loggedInHeaderLinks,
  [DynamicAssetsKeys.headerNavLinks]: navLinks,
  [DynamicAssetsKeys.protectItImage]: require('./resources/protect-it-sprint.jpg'),
  [DynamicAssetsKeys.headerMobileCtaComponent]: (props: any) => (
    <HeaderCallToAction {...props}>{HOME_CALL_TO_ACTION}</HeaderCallToAction>
  ),
  [DynamicAssetsKeys.homeCallToAction]: HOME_CALL_TO_ACTION,

  [DynamicAssetsKeys.footerPartnerLogoImage]: require('./resources/sprint-logo-white.svg'),
  [DynamicAssetsKeys.footerPartnerLogoHref]: 'https://www.sprint.com/',

  [DynamicAssetsKeys.footerOnlineClaimButtonEnabled]: false,
  [DynamicAssetsKeys.confirmationPageDetailsLogo]: require('./resources/scmh-logo.svg'),

  [DynamicAssetsKeys.productName]: PRODUCT_NAME,

  [DynamicAssetsKeys.finePrintIcon1]: getPartnerResource(DynamicAssetsKeys.finePrintIcon1),
  [DynamicAssetsKeys.finePrintIcon2]: getPartnerResource(DynamicAssetsKeys.finePrintIcon2),
  [DynamicAssetsKeys.finePrintIcon3]: getPartnerResource(DynamicAssetsKeys.finePrintIcon3),

  [DynamicAssetsKeys.homeHeroSpacerComponent]: HeroContentSpacer,
  [DynamicAssetsKeys.homeHeroTitle]: <>When it comes to your home technology, we&rsquo;ve got your back.</>,
  [DynamicAssetsKeys.homeHeroTitleComponent]: HeroTitle,
  [DynamicAssetsKeys.homeHeroSubtitle]: 'A revolutionary new plan that covers it all for just $25 a month.',
  [DynamicAssetsKeys.homeHeroSubtitleComponent]: HeroSubtitle,
  [DynamicAssetsKeys.homeHeroImage]: getPartnerResource(DynamicAssetsKeys.homeHeroImage),
  [DynamicAssetsKeys.homeHeroImageContainerComponent]: HeroImageContainer,

  [DynamicAssetsKeys.valueCalculatorHeading1]: (
    <>
      The average home has invested over {formatDollars(4000, 0)} <DisclaimerLink>1</DisclaimerLink> in home tech.
    </>
  ),
  [DynamicAssetsKeys.valueCalculatorHeading2]: 'How do you compare?',
  [DynamicAssetsKeys.valueCalculatorHeadingComponent]: CalculatorHeader,
  [DynamicAssetsKeys.valueCalculatorSubtitle]: 'Select all the devices in your home and see.',

  [DynamicAssetsKeys.homeProtectItTitleComponent]: ProtectItTitle,
  [DynamicAssetsKeys.homeProtectItTitle]: <>You invested, now protect it</>,
  [DynamicAssetsKeys.homeProtectItSubtitle]: (
    <>
      Individual protection plans can be limited and they&rsquo;re a hassle to keep up with. We&rsquo;ve thrown out the
      old model to give you more for less: protection, tech support, and none of the runaround.
    </>
  ),
  [DynamicAssetsKeys.homeProtectItSubtitleComponent]: ProtectItSubtitle,
  [DynamicAssetsKeys.homeProtectItSubtitle2]: 'For just $25/month',
  [DynamicAssetsKeys.homeProtectItCtaComponent]: (props: any) => (
    <ProtectItCta {...props}>{HOME_CALL_TO_ACTION}</ProtectItCta>
  ),
  [DynamicAssetsKeys.homeProtectItTextContainer]: ProtectItTextContainer,
  [DynamicAssetsKeys.homeProtectItContainer]: ProtectItContainer,

  [DynamicAssetsKeys.homeValuePropTitleComponent]: ValuePropTitle,
  [DynamicAssetsKeys.homeValuePropImageContainer]: ValuePropositionImageContainer,
  [DynamicAssetsKeys.homeValuePropContent]: valuePropositionContent,

  [DynamicAssetsKeys.calculatorResultsHeadingComponent]: CalculatorResultsHeader,
  [DynamicAssetsKeys.calculatorResultsHeading]: `${PRODUCT_NAME}. Designed to give you more.`,
  [DynamicAssetsKeys.calculatorResultsSubheading]: '',
  [DynamicAssetsKeys.calculatorResultsIndividualHeading]: 'Individual protection plans',
  [DynamicAssetsKeys.calculatorResultsTableData]: (maxPrice: number) => [
    [<>When can I enroll?</>, <>Anytime</>, <>Only at point of sale</>],
    [<>But, what if my TV is 3 years old?</>, <>It’s covered!</>, <>Out of luck</>],
    [<>What if I buy something new?</>, <>Automatically covered</>, <>Another contract, another payment</>],
    [<>Do I need to register or keep receipts?</>, <>Nope</>, <>Yes</>],
    [<>On demand technical support</>, <>Included!</>, <>+$200 additional</>],
    [<>Learning with experts</>, <>Included!</>, <>Up to $99/session</>],
    [<>How much does protection cost?</>, <>Only $25/month</>, <CompetitorPriceCell maxPrice={maxPrice} />],
  ],
  [DynamicAssetsKeys.calculatorResultsLogoComponent]: CalculatorLogo,
  [DynamicAssetsKeys.calculatorDisclaimer2]: `Based on your answers from above. Individual protection plan estimates
    based on total upfront costs for available protection plans.`,

  [DynamicAssetsKeys.finePrintTitleComponent]: FinePrintTitle,
  [DynamicAssetsKeys.finePrintTitle]: 'The fine print. Magnified.',
  [DynamicAssetsKeys.finePrintSection1Title]: 'Is my phone covered?',
  [DynamicAssetsKeys.finePrintSection1Detail]: (
    <>
      Sprint Complete covers your phone. {PRODUCT_NAME} is for virtually all other home tech that doesn&rsquo;t use
      cellular connectivity or an established phone number.
    </>
  ),
  [DynamicAssetsKeys.finePrintSection2Title]: 'Any hidden fees or deductibles?',
  [DynamicAssetsKeys.finePrintSection2Details]: 'No hidden fees. Some products require a $49 or $89 service fee',
  [DynamicAssetsKeys.finePrintSection3Title]: 'Is there a claim limit?',
  [DynamicAssetsKeys.finePrintSection3Details]: (
    <>File as many claims as you need. Maximum coverage is $2,000/claim, and $5,000/year.</>
  ),
  [DynamicAssetsKeys.finePrintLinkEnabled]: true,
  [DynamicAssetsKeys.finePrintLinkText]: 'See terms and conditions for complete details',
  [DynamicAssetsKeys.finePrintLinkTo]: '/terms-conditions',
  [DynamicAssetsKeys.finePrintLinkLabel]: 'Terms & Conditions',

  [DynamicAssetsKeys.testimonialTitleComponent]: TestimonialTitle,
  [DynamicAssetsKeys.testimonialTitle]: 'Powered by Asurion',
  [DynamicAssetsKeys.testimonialSubtitle]: 'Who we are.',
  [DynamicAssetsKeys.testimonialBody]: (
    <>
      For more than 20 years, Asurion has helped millions of mobile phone customers recover from cracked screens, liquid
      damage and device malfunctions, with a 4.8 average rating.
      <br />
      <br />
      <b>Sprint Complete MyHome by Asurion</b> extends Asurion’s tried-and-true protection and support services to cover
      the tech in your home.
    </>
  ),

  [DynamicAssetsKeys.signupSubtitleComponent]: SignupSubtitle,
  [DynamicAssetsKeys.signupTitleComponent]: EnrollmentTitle,
  [DynamicAssetsKeys.signupImageComponent]: SignupImageContainer,
  [DynamicAssetsKeys.signupPricingComponent]: SignupPricingComponent,

  [DynamicAssetsKeys.signupValueProp1]: 'Worry-free protection',
  [DynamicAssetsKeys.signupValueProp2]: 'Experts you can trust and understand',
  [DynamicAssetsKeys.signupValueProp3]: 'All of the coverage, none of the hassle',

  [DynamicAssetsKeys.signupImage1]: signupImage1,
  [DynamicAssetsKeys.signupImage2]: signupImage2,
  [DynamicAssetsKeys.signupImage3]: signupImage3,
  [DynamicAssetsKeys.signupImage4]: signupImage4,
  [DynamicAssetsKeys.signupImage5]: signupImage5,

  [DynamicAssetsKeys.contactSubmitButtonId]: 'contact-submit-scmh',
  [DynamicAssetsKeys.residenceSubmitButtonId]: 'residence-submit-scmh',
  [DynamicAssetsKeys.billingSubmitButtonId]: 'billing-submit-scmh',
  [DynamicAssetsKeys.reviewSubmitButtonId]: 'enroll-submit-scmh',
  [DynamicAssetsKeys.confirmationCallButtonId]: 'confirmation-call-button-id-scmh',
  [DynamicAssetsKeys.confirmationChatButtonId]: 'confirmation-chat-button-id-scmh',
  [DynamicAssetsKeys.confirmationExpertSessionsButtonId]: 'confirmation-expert-sessions-button-id-scmh',

  // Signup confirmation page
  [DynamicAssetsKeys.confirmationWelcomeMessage]: `It’s official! You’re covered. You now have protection and unlimited
    support for your home technology. You’ll get a welcome kit emailed to you with program details and complete terms
    and conditions.`,
  [DynamicAssetsKeys.confirmationChatHeading]: `Chat with a Tech Expert.`,
  [DynamicAssetsKeys.confirmationChatMessage]: `Your ${PRODUCT_NAME} team is ready to tackle any issue; from setting up
    thermostats and security cams to syncing products and managing remote controls.`,
  [DynamicAssetsKeys.confirmationCallMessage]: `Have questions about your tech at home?`,
  [DynamicAssetsKeys.confirmationCallHeading]: 'Rather call?',
  [DynamicAssetsKeys.confirmationCallComponent]: ConfirmationCallButton,

  [DynamicAssetsKeys.whatsCoveredCallToActionTitle]: 'All your tech, one simple plan.',
  [DynamicAssetsKeys.whatsCoveredCallToActionComponent]: WhatsCoveredCallToActionTitle,
  [DynamicAssetsKeys.whatsCoveredCallToActionInverted]: false,

  [DynamicAssetsKeys.expertChatAppKey]: '1e9a741c-e532-4337-9c7e-c171a6c5f1fc',
  [DynamicAssetsKeys.expertChatSdkEnabled]: true,
};

export default assets;



// WEBPACK FOOTER //
// ./packages/smart-home-support/assets/SHS-SPRINT.tsx